import { GatewayQuestionnaire } from "@conformite/gateway";
import {
  LoadingPage,
  Button,
  Card,
  displayErrorToast,
  displaySucessToast,
  FormGrid,
  UIInputContainer,
} from "@endya-conformite/ui-kit";
import { PageWithTitleTemplate } from "@src/components/templates/PageWithTitleTemplate";
import { useQuestionnaireDefinitionQuery } from "@src/data/questionnaire/hook/useQuestionnaireDefinitionQuery";
import {
  useCreateQuestionnaireDefinitionMutation,
  useUpdateQuestionnaireDefinitionMutation,
} from "@src/data/questionnaire/hook/useSaveQuestionnaireDefinitionMutation";
import { isRecette } from "@src/helper/environnement.helper";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FIL_ARIANE_MES_CONTROLES } from "@src/pages/controle-quinquennal/cqListe.wording";
import styles from "./QuestionnaireAdminEditPage.module.scss";

type EditableQuestion = {
  uid: string;
  label: string;
  helpText: string;
  indicationIA: string;
  order: number;
  options: GatewayQuestionnaire.QuestionOption[];
  required: boolean;
  condition?: GatewayQuestionnaire.QuestionCondition;
  type: GatewayQuestionnaire.QuestionType;
};

const COLOR_OPTIONS: GatewayQuestionnaire.OptionColor[] = [
  "blue",
  "green",
  "orange",
  "red",
];

const INDICATION_IA_MAX_LENGTH = 4000;

const COLOR_LABELS: Record<GatewayQuestionnaire.OptionColor, string> = {
  blue: "Bleu",
  green: "Vert",
  orange: "Orange",
  red: "Rouge",
};

/* ─── Sous-composant ConditionEditor ─── */

type ConditionEditorProps = {
  question: EditableQuestion;
  index: number;
  allQuestions: EditableQuestion[];
  onConditionChange: (
    uid: string,
    condition: GatewayQuestionnaire.QuestionCondition | undefined
  ) => void;
};

function resolveParentQuestion(
  order: number,
  allQuestions: EditableQuestion[]
): EditableQuestion | undefined {
  return allQuestions.find((q) => q.order === order);
}

function ConditionEditor({
  question,
  index,
  allQuestions,
  onConditionChange,
}: ConditionEditorProps) {
  const precedingQuestions = allQuestions.filter((_, i) => i < index);
  const hasCondition = !!question.condition;
  const groups = question.condition?.groups ?? [];

  const emitCondition = (
    newGroups: GatewayQuestionnaire.ConditionRuleGroup[]
  ) => {
    const needsTopAnd = newGroups.length > 1;
    onConditionChange(question.uid, {
      ...(needsTopAnd ? { logic: "AND" as const } : {}),
      groups: newGroups.map((g) => ({
        ...(g.rules.length > 1 ? { logic: "OR" as const } : {}),
        rules: g.rules,
      })),
    });
  };

  const handleToggleCondition = () => {
    if (hasCondition) {
      onConditionChange(question.uid, undefined);
    } else if (precedingQuestions.length > 0) {
      const prev = precedingQuestions[precedingQuestions.length - 1];
      const firstOption = prev.options[0]?.id;
      emitCondition([
        {
          rules: [
            {
              questionOrder: prev.order,
              expectedOptionIds: firstOption ? [firstOption] : [],
            },
          ],
        },
      ]);
    }
  };

  const handleRuleParentChange = (
    groupIndex: number,
    ruleIndex: number,
    value: string
  ) => {
    const newOrder = parseInt(value, 10);
    const newParent = resolveParentQuestion(newOrder, allQuestions);
    const firstOption = newParent?.options[0]?.id;
    const newGroups = groups.map((g, gi) =>
      gi === groupIndex
        ? {
            ...g,
            rules: g.rules.map((r, ri) =>
              ri === ruleIndex
                ? {
                    questionOrder: newOrder,
                    expectedOptionIds: firstOption ? [firstOption] : [],
                  }
                : r
            ),
          }
        : g
    );
    emitCondition(newGroups);
  };

  const handleRuleValuesChange = (
    groupIndex: number,
    ruleIndex: number,
    optionLabel: string
  ) => {
    const currentValues =
      groups[groupIndex]?.rules[ruleIndex]?.expectedOptionIds ?? [];
    const current = new Set(currentValues);
    if (current.has(optionLabel)) {
      current.delete(optionLabel);
    } else {
      current.add(optionLabel);
    }
    const newGroups = groups.map((g, gi) =>
      gi === groupIndex
        ? {
            ...g,
            rules: g.rules.map((r, ri) =>
              ri === ruleIndex
                ? { ...r, expectedOptionIds: Array.from(current) }
                : r
            ),
          }
        : g
    );
    emitCondition(newGroups);
  };

  const handleAddRule = (groupIndex: number) => {
    if (precedingQuestions.length === 0) return;
    const prev = precedingQuestions[precedingQuestions.length - 1];
    const firstOption = prev.options[0]?.id;
    const newGroups = groups.map((g, gi) =>
      gi === groupIndex
        ? {
            ...g,
            rules: [
              ...g.rules,
              {
                questionOrder: prev.order,
                expectedOptionIds: firstOption ? [firstOption] : [],
              },
            ],
          }
        : g
    );
    emitCondition(newGroups);
  };

  const handleRemoveRule = (groupIndex: number, ruleIndex: number) => {
    const group = groups[groupIndex];
    if (group.rules.length <= 1) {
      // Remove entire group
      const newGroups = groups.filter((_, gi) => gi !== groupIndex);
      if (newGroups.length === 0) {
        onConditionChange(question.uid, undefined);
      } else {
        emitCondition(newGroups);
      }
    } else {
      const newGroups = groups.map((g, gi) =>
        gi === groupIndex
          ? { ...g, rules: g.rules.filter((_, ri) => ri !== ruleIndex) }
          : g
      );
      emitCondition(newGroups);
    }
  };

  const handleAddGroup = () => {
    if (precedingQuestions.length === 0) return;
    const prev = precedingQuestions[precedingQuestions.length - 1];
    const firstOption = prev.options[0]?.id;
    emitCondition([
      ...groups,
      {
        rules: [
          {
            questionOrder: prev.order,
            expectedOptionIds: firstOption ? [firstOption] : [],
          },
        ],
      },
    ]);
  };

  if (index === 0) return null;

  return (
    <div className={styles.conditionField}>
      <div className={styles.conditionToggle}>
        <label className={styles.conditionToggleLabel}>
          <input
            type="checkbox"
            checked={hasCondition}
            onChange={handleToggleCondition}
          />
          <span>Question conditionnelle</span>
        </label>
      </div>

      {hasCondition && (
        <div className={styles.conditionConfig}>
          {groups.map((group, groupIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={groupIndex}>
              {groupIndex > 0 && (
                <div className={styles.conditionLogicSeparator}>
                  <span className={styles.conditionLogicBadge}>ET</span>
                </div>
              )}
              <div className={styles.conditionGroup}>
                {group.rules.map((rule, ruleIndex) => {
                  const parentQuestion = resolveParentQuestion(
                    rule.questionOrder,
                    allQuestions
                  );
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={ruleIndex}>
                      {ruleIndex > 0 && (
                        <div className={styles.conditionLogicSeparator}>
                          <span className={styles.conditionLogicBadgeOr}>
                            OU
                          </span>
                        </div>
                      )}
                      <div className={styles.conditionRuleBlock}>
                        <div className={styles.conditionRow}>
                          <span className={styles.conditionLabel}>
                            {groupIndex === 0 && ruleIndex === 0
                              ? "Afficher si"
                              : ""}
                          </span>
                          <select
                            className={styles.conditionSelect}
                            value={rule.questionOrder.toString()}
                            onChange={(e) =>
                              handleRuleParentChange(
                                groupIndex,
                                ruleIndex,
                                e.target.value
                              )
                            }
                          >
                            {precedingQuestions.map((q) => (
                              <option key={q.uid} value={q.order.toString()}>
                                Q{q.order} — {q.label.slice(0, 50)}
                                {q.label.length > 50 ? "…" : ""}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className={styles.conditionRemoveRuleBtn}
                            onClick={() =>
                              handleRemoveRule(groupIndex, ruleIndex)
                            }
                            title="Supprimer cette condition"
                          >
                            ×
                          </button>
                        </div>

                        {parentQuestion &&
                          parentQuestion.options.length > 0 && (
                            <div className={styles.conditionRow}>
                              <span className={styles.conditionLabel}>
                                vaut
                              </span>
                              <div className={styles.conditionValues}>
                                {parentQuestion.options.map((opt) => (
                                  <label
                                    key={opt.id}
                                    className={styles.conditionValueChip}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={rule.expectedOptionIds.includes(
                                        opt.id
                                      )}
                                      onChange={() =>
                                        handleRuleValuesChange(
                                          groupIndex,
                                          ruleIndex,
                                          opt.id
                                        )
                                      }
                                    />
                                    <span>{opt.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className={styles.conditionAddBtn}
                  onClick={() => handleAddRule(groupIndex)}
                >
                  + Ajouter une condition OU
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className={styles.conditionAddGroupBtn}
            onClick={handleAddGroup}
          >
            + Ajouter un groupe ET
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Sous-composant QuestionEditor ─── */

type QuestionEditorProps = {
  question: EditableQuestion;
  index: number;
  total: number;
  allQuestions: EditableQuestion[];
  onUpdate: (
    uid: string,
    field: keyof EditableQuestion,
    value: unknown
  ) => void;
  onRemove: (uid: string) => void;
  onMove: (index: number, direction: -1 | 1) => void;
  onAddOption: (uid: string, option: string) => void;
  onRemoveOption: (uid: string, optionLabel: string) => void;
  onUpdateOptionColor: (
    uid: string,
    optionLabel: string,
    color: GatewayQuestionnaire.OptionColor
  ) => void;
  onConditionChange: (
    uid: string,
    condition: GatewayQuestionnaire.QuestionCondition | undefined
  ) => void;
};

function QuestionEditor({
  question,
  index,
  total,
  allQuestions,
  onUpdate,
  onRemove,
  onMove,
  onAddOption,
  onRemoveOption,
  onUpdateOptionColor,
  onConditionChange,
}: QuestionEditorProps) {
  const [newOption, setNewOption] = useState("");

  const handleAddOption = () => {
    if (newOption.trim()) {
      onAddOption(question.uid, newOption.trim());
      setNewOption("");
    }
  };

  return (
    <div className={styles.questionItem}>
      {/* En-tête de la question */}
      <div className={styles.questionHeader}>
        <div className={styles.questionHeaderLeft}>
          <span className={styles.questionBadge}>Q{index + 1}</span>
          <div className={styles.moveButtons}>
            <button
              className={styles.moveBtn}
              onClick={() => onMove(index, -1)}
              disabled={index === 0}
              title="Monter"
              type="button"
            >
              ▲
            </button>
            <button
              className={styles.moveBtn}
              onClick={() => onMove(index, 1)}
              disabled={index === total - 1}
              title="Descendre"
              type="button"
            >
              ▼
            </button>
          </div>
          {question.condition && (
            <span className={styles.conditionBadge}>Conditionnelle</span>
          )}
        </div>
        <Button variant="text" onClick={() => onRemove(question.uid)}>
          Supprimer
        </Button>
      </div>

      {/* Corps de la question */}
      <div className={styles.questionBody}>
        <UIInputContainer
          name={`question-${question.uid}-label`}
          label="Libellé de la question"
          required
          hideError
          size="large"
        >
          <textarea
            id={`question-${question.uid}-label`}
            className={styles.textarea}
            value={question.label}
            onChange={(e) => onUpdate(question.uid, "label", e.target.value)}
            placeholder="Texte de la question affiché aux vérificateurs..."
            rows={2}
          />
        </UIInputContainer>

        <UIInputContainer
          name={`question-${question.uid}-helpText`}
          label="Texte d'aide (optionnel)"
          hideError
          size="large"
        >
          <input
            id={`question-${question.uid}-helpText`}
            className={styles.input}
            value={question.helpText}
            onChange={(e) => onUpdate(question.uid, "helpText", e.target.value)}
            placeholder="Tooltip ou précision affichés sous le libellé..."
          />
        </UIInputContainer>

        <UIInputContainer
          name={`question-${question.uid}-type`}
          label="Type de réponse"
          hideError
          size="large"
        >
          <select
            id={`question-${question.uid}-type`}
            className={styles.input}
            value={question.type}
            onChange={(e) =>
              onUpdate(
                question.uid,
                "type",
                e.target.value as GatewayQuestionnaire.QuestionType
              )
            }
          >
            <option value="single">Réponse unique</option>
            <option value="multiple">Plusieurs réponses possibles</option>
          </select>
        </UIInputContainer>

        {/* Feature IA restreinte à la recette : le champ est masqué ailleurs
            mais la valeur reste dans le state et le payload de sauvegarde. */}
        {isRecette && (
          <>
            <UIInputContainer
              name={`question-${question.uid}-indicationIA`}
              label="Indication pour l'IA (optionnel)"
              hideError
              size="large"
            >
              <textarea
                id={`question-${question.uid}-indicationIA`}
                className={styles.textarea}
                value={question.indicationIA}
                onChange={(e) =>
                  onUpdate(question.uid, "indicationIA", e.target.value)
                }
                placeholder="Instructions données à l'IA pour analyser les documents et répondre à cette question..."
                rows={2}
                maxLength={INDICATION_IA_MAX_LENGTH}
              />
            </UIInputContainer>
            <p className={styles.charCounter}>
              {question.indicationIA.length}/{INDICATION_IA_MAX_LENGTH}
            </p>
          </>
        )}

        {/* Options de réponse */}
        <div className={styles.optionsField}>
          <span className={styles.optionsLabel}>Options de réponse</span>
          <div className={styles.optionsRow}>
            {question.options.map((opt) => (
              <div
                key={opt.label}
                className={styles.optionChip}
                data-color={opt.color ?? "none"}
              >
                <span className={styles.optionChipLabel}>{opt.label}</span>

                {/* Sélecteur de couleur */}
                <div className={styles.colorSwatchRow}>
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={styles.colorSwatch}
                      data-color={color}
                      data-selected={opt.color === color ? "true" : "false"}
                      onClick={(e) => {
                        e.preventDefault();
                        onUpdateOptionColor(question.uid, opt.label, color);
                      }}
                      title={COLOR_LABELS[color]}
                    />
                  ))}
                </div>

                <button
                  className={styles.removeOptionBtn}
                  onClick={() => onRemoveOption(question.uid, opt.label)}
                  type="button"
                  title={`Supprimer l'option "${opt.label}"`}
                >
                  ×
                </button>
              </div>
            ))}
            <div className={styles.addOptionRow}>
              <input
                className={styles.addOptionInput}
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddOption();
                  }
                }}
                placeholder="+ Ajouter une option"
              />
              {newOption.trim() && (
                <button
                  className={styles.addOptionConfirmBtn}
                  onClick={handleAddOption}
                  type="button"
                >
                  Ajouter
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Condition de visibilité */}
        <ConditionEditor
          question={question}
          index={index}
          allQuestions={allQuestions}
          onConditionChange={onConditionChange}
        />
      </div>
    </div>
  );
}

/* ─── Helpers ─── */

/**
 * Remap questionOrder references in a condition after questions have been
 * reordered or removed. `orderMap` maps old order → new order.
 * If `removedOrder` is provided, any rule referencing it is dropped;
 * empty groups / conditions are cleaned up to `undefined`.
 */
function remapConditionOrders(
  condition: GatewayQuestionnaire.QuestionCondition | undefined,
  orderMap: Map<number, number>,
  removedOrder?: number
): GatewayQuestionnaire.QuestionCondition | undefined {
  if (!condition) return undefined;

  const newGroups = condition.groups
    .map((group) => {
      const newRules = group.rules
        .filter(
          (r) => r.questionOrder === -1 || r.questionOrder !== removedOrder
        )
        .map((r) => {
          if (r.questionOrder === -1) return r;
          const mapped = orderMap.get(r.questionOrder);
          return mapped !== undefined ? { ...r, questionOrder: mapped } : r;
        });
      if (newRules.length === 0) return null;
      return { ...group, rules: newRules };
    })
    .filter((g): g is GatewayQuestionnaire.ConditionRuleGroup => g !== null);

  if (newGroups.length === 0) return undefined;
  return { ...condition, groups: newGroups };
}

/* ─── Page principale ─── */

export function QuestionnaireAdminEditPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isNew = slug === "new";

  const { data: definition, isLoading } = useQuestionnaireDefinitionQuery(
    isNew ? undefined : slug
  );

  const createMutation = useCreateQuestionnaireDefinitionMutation();
  const updateMutation = useUpdateQuestionnaireDefinitionMutation();

  const [name, setName] = useState("");
  const [slugValue, setSlugValue] = useState<
    GatewayQuestionnaire.QuestionnaireSlug | ""
  >("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState<EditableQuestion[]>([]);
  const [initialized, setInitialized] = useState(isNew);

  // Snapshot sérialisé de l'état enregistré, pour détecter les modifications
  // (bouton Enregistrer / Annuler) et pour réinitialiser le formulaire.
  const initialSnapshotRef = useRef<string | null>(
    isNew
      ? JSON.stringify({
          name: "",
          slugValue: "",
          description: "",
          questions: [],
        })
      : null
  );

  useEffect(() => {
    if (definition && !initialized) {
      const loadedQuestions: EditableQuestion[] = [...definition.questions]
        .sort((a, b) => a.order - b.order)
        .map((q) => ({
          uid: q.id,
          label: q.label,
          helpText: q.helpText ?? "",
          indicationIA: q.indicationIA ?? "",
          order: q.order,
          options: q.options,
          required: q.required,
          condition: q.condition ?? undefined,
          type: q.type ?? "single",
        }));
      setName(definition.name);
      setSlugValue(definition.slug);
      setDescription(definition.description ?? "");
      setQuestions(loadedQuestions);
      initialSnapshotRef.current = JSON.stringify({
        name: definition.name,
        slugValue: definition.slug,
        description: definition.description ?? "",
        questions: loadedQuestions,
      });
      setInitialized(true);
    }
  }, [definition, initialized]);

  const addQuestion = useCallback(() => {
    setQuestions((prev) => [
      ...prev,
      {
        uid: crypto.randomUUID(),
        label: "",
        helpText: "",
        indicationIA: "",
        order: prev.length + 1,
        options: [
          {
            id: crypto.randomUUID(),
            label: "Oui",
            color: "green" as const,
            weight: 0,
            order: 0,
          },
          {
            id: crypto.randomUUID(),
            label: "Non",
            color: "red" as const,
            weight: 0,
            order: 1,
          },
          {
            id: crypto.randomUUID(),
            label: "Manquant",
            color: "orange" as const,
            weight: 0,
            order: 2,
          },
        ],
        required: true,
        type: "single",
      },
    ]);
  }, []);

  const updateQuestion = useCallback(
    (uid: string, field: keyof EditableQuestion, value: unknown) => {
      setQuestions((prev) =>
        prev.map((q) => (q.uid === uid ? { ...q, [field]: value } : q))
      );
    },
    []
  );

  const removeQuestion = useCallback((uid: string) => {
    setQuestions((prev) => {
      const removedOrder = prev.find((q) => q.uid === uid)?.order;
      const filtered = prev.filter((q) => q.uid !== uid);
      // Build old→new order mapping
      const orderMap = new Map<number, number>();
      filtered.forEach((q, i) => {
        orderMap.set(q.order, i + 1);
      });
      return filtered.map((q, i) => ({
        ...q,
        order: i + 1,
        condition: remapConditionOrders(q.condition, orderMap, removedOrder),
      }));
    });
  }, []);

  const moveQuestion = useCallback((index: number, direction: -1 | 1) => {
    setQuestions((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      // Build old→new order mapping
      const orderMap = new Map<number, number>();
      next.forEach((q, i) => {
        orderMap.set(q.order, i + 1);
      });
      return next.map((q, i) => ({
        ...q,
        order: i + 1,
        condition: remapConditionOrders(q.condition, orderMap),
      }));
    });
  }, []);

  const addOption = useCallback((uid: string, optionLabel: string) => {
    const trimmed = optionLabel.trim();
    if (!trimmed) return;
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.uid !== uid) return q;
        if (q.options.some((opt) => opt.label === trimmed)) {
          displayErrorToast(`L'option "${trimmed}" existe déjà`);
          return q;
        }
        return {
          ...q,
          options: [
            ...q.options,
            {
              id: crypto.randomUUID(),
              label: trimmed,
              weight: 0,
              order: q.options.length,
            },
          ],
        };
      })
    );
  }, []);

  const removeOption = useCallback((uid: string, optionLabel: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.uid === uid
          ? {
              ...q,
              options: q.options.filter((opt) => opt.label !== optionLabel),
            }
          : q
      )
    );
  }, []);

  const updateOptionColor = useCallback(
    (
      uid: string,
      optionLabel: string,
      color: GatewayQuestionnaire.OptionColor
    ) => {
      setQuestions((prev) =>
        prev.map((q) =>
          q.uid === uid
            ? {
                ...q,
                options: q.options.map((opt) =>
                  opt.label === optionLabel ? { ...opt, color } : opt
                ),
              }
            : q
        )
      );
    },
    []
  );

  const updateCondition = useCallback(
    (
      uid: string,
      condition: GatewayQuestionnaire.QuestionCondition | undefined
    ) => {
      setQuestions((prev) =>
        prev.map((q) => (q.uid === uid ? { ...q, condition } : q))
      );
    },
    []
  );

  const handleSave = async () => {
    const questionsPayload: GatewayQuestionnaire.CreateQuestionDefinitionRequestType[] =
      questions.map((q, i) => ({
        id: q.uid,
        label: q.label,
        helpText: q.helpText || undefined,
        // "" et non undefined : le back mappe le vide vers null pour effacer l'indication.
        indicationIA: q.indicationIA ?? "",
        order: i + 1,
        options: q.options.map((opt, optIdx) => ({
          id: opt.id,
          label: opt.label,
          color: opt.color,
          weight: opt.weight ?? 0,
          order: opt.order ?? optIdx,
        })),
        required: q.required,
        condition: q.condition,
        type: q.type,
      }));

    try {
      if (isNew && slugValue) {
        await createMutation.mutateAsync({
          slug: slugValue,
          name,
          description: description || undefined,
          questions: questionsPayload,
        });
      } else if (definition) {
        await updateMutation.mutateAsync({
          id: definition.id,
          body: {
            name,
            description: description || undefined,
            questions: questionsPayload,
          },
        });
      }
      displaySucessToast("Questionnaire enregistré avec succès");
      navigate("/admin/questionnaires");
    } catch {
      displayErrorToast("Une erreur est survenue lors de l'enregistrement");
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const isDirty =
    initialSnapshotRef.current !== null &&
    JSON.stringify({ name, slugValue, description, questions }) !==
      initialSnapshotRef.current;

  const handleReset = () => {
    if (initialSnapshotRef.current === null) return;
    const snapshot = JSON.parse(initialSnapshotRef.current) as {
      name: string;
      slugValue: GatewayQuestionnaire.QuestionnaireSlug | "";
      description: string;
      questions: EditableQuestion[];
    };
    setName(snapshot.name);
    setSlugValue(snapshot.slugValue);
    setDescription(snapshot.description);
    setQuestions(snapshot.questions);
  };

  if (!isNew && (isLoading || !initialized))
    return (
      <PageWithTitleTemplate title="Chargement...">
        <LoadingPage withText={false} />
      </PageWithTitleTemplate>
    );

  const breadcrumbElements = [
    FIL_ARIANE_MES_CONTROLES,
    { label: "Administration des formulaires", value: "/admin/questionnaires" },
    {
      label: isNew ? "Nouveau questionnaire" : name,
      value: "current",
    },
  ];

  return (
    <PageWithTitleTemplate
      title={isNew ? "Nouveau questionnaire" : name}
      breadcrumb={{
        items: breadcrumbElements,
        setSelected: (nextRoute) => {
          if (nextRoute !== "current") navigate(nextRoute);
        },
        selected: "current",
      }}
    >
      <div className={styles.container}>
        {/* ─── Métadonnées ─── */}
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <p className={styles.cardSurtitle}>Informations générales</p>
            <h4 className={styles.cardTitle}>
              {isNew ? "Nouveau questionnaire" : name || "—"}
            </h4>
          </div>
          <div className={styles.separator} />

          <FormGrid disableHorizontalGutter>
            <div className={styles.fieldWrapper}>
              <UIInputContainer
                name="name"
                label="Nom du questionnaire"
                required
                hideError
                size="large"
              >
                <input
                  id="name"
                  className={styles.input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex : RC Professionnelle COA"
                />
              </UIInputContainer>
            </div>

            <div className={styles.fieldWrapper}>
              <UIInputContainer
                name="slug"
                label="Slug"
                required
                hideError
                size="large"
              >
                <select
                  id="slug"
                  className={styles.input}
                  value={slugValue}
                  onChange={(e) =>
                    setSlugValue(
                      (e.target
                        .value as GatewayQuestionnaire.QuestionnaireSlug) || ""
                    )
                  }
                  disabled={!isNew}
                >
                  <option value="">Sélectionner un slug</option>
                  {Object.values(GatewayQuestionnaire.QuestionnaireSlug).map(
                    (s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    )
                  )}
                </select>
              </UIInputContainer>
              {!isNew && (
                <p className={styles.fieldHint}>
                  Non modifiable après création
                </p>
              )}
            </div>

            <div className={styles.fieldWrapper}>
              <UIInputContainer
                name="description"
                label="Description"
                hideError
                size="large"
              >
                <textarea
                  id="description"
                  className={styles.textarea}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description optionnelle du questionnaire..."
                  rows={2}
                />
              </UIInputContainer>
            </div>
          </FormGrid>
        </Card>

        {/* ─── Questions ─── */}
        <Card className={styles.card}>
          <div className={styles.questionsCardHeader}>
            <div>
              <p className={styles.cardSurtitle}>Contenu</p>
              <h4 className={styles.cardTitle}>
                Questions{" "}
                <span className={styles.questionCount}>{questions.length}</span>
              </h4>
            </div>
            <Button variant="outline" onClick={addQuestion}>
              + Ajouter une question
            </Button>
          </div>

          <div className={styles.separator} />

          {questions.length === 0 ? (
            <div className={styles.emptyQuestions}>
              <p>
                Aucune question. Cliquez sur &ldquo;Ajouter une question&rdquo;
                pour commencer.
              </p>
            </div>
          ) : (
            <div className={styles.questionsList}>
              {questions.map((question, index) => (
                <QuestionEditor
                  key={question.uid}
                  question={question}
                  index={index}
                  total={questions.length}
                  allQuestions={questions}
                  onUpdate={updateQuestion}
                  onRemove={removeQuestion}
                  onMove={moveQuestion}
                  onAddOption={addOption}
                  onRemoveOption={removeOption}
                  onUpdateOptionColor={updateOptionColor}
                  onConditionChange={updateCondition}
                />
              ))}
            </div>
          )}
        </Card>

        {/* ─── Actions ─── */}
        <div className={styles.pageActions}>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/questionnaires")}
          >
            Quitter
          </Button>
          <div className={styles.pageActionsRight}>
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={isSaving || !isDirty}
            >
              Réinitialiser
            </Button>
            <Button
              variant="filled"
              onClick={() => {
                void handleSave();
              }}
              disabled={isSaving || !name || !slugValue || !isDirty}
            >
              {isSaving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </div>
      </div>
    </PageWithTitleTemplate>
  );
}
